import React, { useState } from 'react';
import {
  BackButton,
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { TripRequest, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Skeleton,
  Typography,
  Stack,
} from '@mui/material';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { SubmitHandler, FieldValues } from 'react-hook-form';
import TripRequestReferralForm from './trip-request-referral-form';

const TripRequestReferral = () => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { tripRequestId } = useParams();

  const {
    data: tripRequest, isLoading, error: trError, mutate,
  } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], true);

  const [submissionError, setSubmissionError] = useState<ApiError>();

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (tripRequest) {
      const tr: TripRequest = {
        ...tripRequest,
        status: TripRequestStatus.ReferredStatus,
        statusInfo: {
          ...tripRequest.statusInfo,
          reason: `${data.referTo}:${data.referralComments}`,
        },
      };
      try {
        await apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId!, tr);
        mutate();
        enqueueSnackbar('Referral created', { variant: 'success' });
        navigate(`/trip-requests/${tripRequestId}`);
      } catch (e: any) {
        if (e.code === 401) navigate('/logout');
        setSubmissionError(e);
        enqueueSnackbar('An error setting referral', { variant: 'default' });
      }
    }
  };

  const onCancel = () => navigate(`/trip-requests/${tripRequestId}`);

  return (
    <Container maxWidth="sm">
      <Box sx={{ pt: 6, pb: 7 }}>
        <BackButton
          aria-label="back to trip"
          defaultPath={`/trip-requests/${tripRequestId}`}
        />
        <Typography variant="h2" width="100%" textAlign="left" position="relative" pt="20px">
          Referral
        </Typography>
        <Typography variant="body1" width="100%" textAlign="left" position="relative" pt="20px">
          This will mark the trip request as a referral. It will no longer be available for matching.
        </Typography>
        {(trError) && (
        <Alert severity="error">
          <AlertTitle>There was an error on our end.</AlertTitle>
          {`Code: ${(trError).code}: ${(trError).message}`}
          <br />
          {(trError).id && `ID: ${(trError).id}`}
        </Alert>
        )}
        {(isLoading) ? (
          <Stack spacing={2} mt="20px">
            {Array(10).fill(null).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={index} variant="rounded" height="43px" width="100%" />
            ))}
          </Stack>
        ) : (
          <TripRequestReferralForm
            onCancel={onCancel}
            onSubmit={onSubmit}
            submissionError={submissionError}
          />
        )}
      </Box>
    </Container>

  );
};

export default TripRequestReferral;
